import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Trophy,
  DollarSign,
  Users,
  Heart,
  Crown,
  BarChart3,
  Rocket,
  ArrowRight,
  Medal,
} from 'lucide-react';
import api, { getFullImageUrl } from '../services/api';
import './Leaderboard.css';

const PRIZES = {
  profit: ['$100,000', '$80,000', '$50,000'],
  followers: ['$50,000', '$30,000', '$10,000'],
  likes: ['$10,000', '$8,000', '$5,000'],
};

const TABS = [
  { id: 'profit', label: 'Total Profit', icon: DollarSign },
  { id: 'followers', label: 'Followers', icon: Users },
  { id: 'likes', label: 'Likes', icon: Heart },
];

const formatCount = (count) => {
  const num = typeof count === 'number' ? count : parseInt(count || 0, 10);
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatCompactMoney = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (num >= 10000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getAvatarUrl = (avatar, index) => {
  const fallback = `https://images.unsplash.com/photo-${1500000000000 + (index || 0)}?w=200&h=200&fit=crop&crop=face`;
  return getFullImageUrl(avatar, fallback);
};

const getMetricValue = (trader, tab) => {
  if (tab === 'followers') return trader.followers;
  if (tab === 'likes') return trader.likes;
  return trader.profit;
};

const getMetricDisplay = (trader, tab) => {
  if (tab === 'followers') return formatCount(trader.followers);
  if (tab === 'likes') return formatCount(trader.likes);
  return formatCompactMoney(trader.profit);
};

const getMetricUnit = (tab) => {
  if (tab === 'followers') return 'Followers';
  if (tab === 'likes') return 'Likes';
  return 'Total Profit';
};

const isExternalLink = (link) => /^https?:\/\//i.test(String(link || '').trim());

const normalizeInternalLink = (link) => {
  const trimmed = String(link || '').trim();
  if (!trimmed) return '/register';
  if (trimmed.startsWith('/')) return trimmed;
  return `/${trimmed}`;
};

const Leaderboard = () => {
  const [activeTab, setActiveTab] = useState('profit');
  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ctaLink, setCtaLink] = useState('/register');

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    const loadCtaLink = async () => {
      try {
        const response = await api.get('/settings/leaderboardCtaLink');
        if (response.data.success && response.data.data) {
          setCtaLink(String(response.data.data).trim());
        }
      } catch (error) {
        console.error('Failed to fetch leaderboard CTA link:', error);
      }
    };
    loadCtaLink();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await api.get('/traders', {
        params: { limit: 20, sortBy: 'totalProfit', sortOrder: 'DESC' },
      });
      if (response.data.success) {
        const tradersData = response.data.data.traders || [];
        const mappedTraders = tradersData.map((item, index) => ({
          id: item.id || index,
          rank: item.rank || index + 1,
          name: item.name || 'Unknown',
          title: item.title || 'Trader',
          avatar: getAvatarUrl(item.avatar, index),
          profit: parseFloat(item.totalProfit) || 0,
          profitFormatted: `$${(parseFloat(item.totalProfit) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
          trades: item.totalTrades || 0,
          winRate: parseFloat(item.winRate) || 0,
          followers: typeof item.followers === 'number' ? item.followers : parseInt(item.followers || 0, 10),
          likes: typeof item.likes === 'number' ? item.likes : parseInt(item.likes || 0, 10),
        }));
        setTraders(mappedTraders);
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortedTraders = useMemo(() => {
    const list = [...traders].sort((a, b) => getMetricValue(b, activeTab) - getMetricValue(a, activeTab));
    return list.map((t, i) => ({ ...t, displayRank: i + 1 }));
  }, [traders, activeTab]);

  const topThree = sortedTraders.slice(0, 3);
  const rest = sortedTraders.slice(3);

  const podiumOrder = [
    { trader: topThree[1], variant: 'silver' },
    { trader: topThree[0], variant: 'gold' },
    { trader: topThree[2], variant: 'bronze' },
  ].filter((p) => p.trader);

  if (loading) {
    return (
      <div className="leaderboard-page leaderboard-page--loading premium-page">
        <div className="leaderboard-page__loader">
          <Trophy size={40} className="leaderboard-page__loader-icon" />
          <div className="leaderboard-page__loader-text">Loading leaderboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-page premium-page">
      <div className="leaderboard-page__bg" aria-hidden="true">
        <div className="leaderboard-page__bg-grid" />
        <div className="leaderboard-page__fx-wash" />
        <div className="leaderboard-page__fx-blob leaderboard-page__fx-blob--a" />
        <div className="leaderboard-page__fx-blob leaderboard-page__fx-blob--b" />
        <div className="leaderboard-page__fx-blob leaderboard-page__fx-blob--c" />
        <div className="leaderboard-page__fx-beam leaderboard-page__fx-beam--1" />
        <div className="leaderboard-page__fx-beam leaderboard-page__fx-beam--2" />
        <div className="leaderboard-page__fx-ring" />
        <div className="leaderboard-page__fx-dots">
          {Array.from({ length: 24 }).map((_, i) => (
            <span
              key={i}
              className="leaderboard-page__fx-dot"
              style={{
                '--i': i,
                left: `${4 + ((i * 37) % 92)}%`,
                animationDelay: `${(i % 12) * 0.45}s`,
                animationDuration: `${10 + (i % 7) * 1.4}s`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="leaderboard-page__container">
        <header className="leaderboard-page__hero">
          <div className="leaderboard-page__trophy-wrap">
            <Trophy size={64} className="leaderboard-page__trophy" />
          </div>
          <h1 className="leaderboard-page__title">Trading Champions</h1>
          <p className="leaderboard-page__subtitle">
            Top performers ranked by {getMetricUnit(activeTab).toLowerCase()}
          </p>
        </header>

        <div className="leaderboard-page__tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                className={`leaderboard-page__tab ${active ? 'leaderboard-page__tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {active && (
                  <motion.span
                    className="leaderboard-page__tab-indicator"
                    layoutId="lb-tab-indicator"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <Icon size={18} className="leaderboard-page__tab-icon" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <section className="leaderboard-page__prizes">
          <div className="prizes-banner">
            <div className="prize-card prize-card--silver">
              <Medal size={28} className="prize-card__medal medal-silver" />
              <span className="prize-card__place">2nd</span>
              <span className="prize-card__amount">{PRIZES[activeTab][1]}</span>
            </div>
            <div className="prize-card prize-card--gold">
              <Crown size={20} className="prize-card__crown" />
              <Medal size={32} className="prize-card__medal medal-gold" />
              <span className="prize-card__place">1st</span>
              <span className="prize-card__amount">{PRIZES[activeTab][0]}</span>
            </div>
            <div className="prize-card prize-card--bronze">
              <Medal size={26} className="prize-card__medal medal-bronze" />
              <span className="prize-card__place">3rd</span>
              <span className="prize-card__amount">{PRIZES[activeTab][2]}</span>
            </div>
          </div>
        </section>

        {topThree.length > 0 && (
          <section className="leaderboard-page__podium">
            <div className="leaderboard-page__podium-inner">
              {podiumOrder.map(({ trader, variant }, index) => (
                <article key={`${activeTab}-${trader.id}`} className={`podium-card podium-card--${variant}`}>
                  <span className="glass-shine-wrap" aria-hidden="true">
                    <span
                      className={`glass-shine ${variant === 'gold' ? 'glass-shine--gold' : ''}`}
                      style={{ animationDelay: `${index * 0.9}s` }}
                    />
                  </span>
                  {variant === 'gold' && <Crown size={20} className="podium-card__crown" />}
                  <div className={`podium-card__rank podium-card__rank--${variant}`}>
                    {trader.displayRank}
                  </div>
                  <img src={trader.avatar} alt={trader.name} className="podium-card__avatar" />
                  <h3 className="podium-card__name">{trader.name}</h3>
                  <p className="podium-card__title">{trader.title}</p>
                  <div className="podium-card__metric">
                    <span className="podium-card__metric-label">{getMetricUnit(activeTab)}</span>
                    <span className={`podium-card__value ${variant === 'gold' ? 'podium-card__value--gold' : ''}`}>
                      {getMetricDisplay(trader, activeTab)}
                    </span>
                    {activeTab === 'profit' && trader.profit >= 10000 && (
                      <span className="podium-card__metric-full">{trader.profitFormatted}</span>
                    )}
                  </div>
                  <div className="podium-card__stats">
                    <span>{trader.trades} trades</span>
                    <span>{trader.winRate.toFixed(1)}% win</span>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {rest.length > 0 && (
          <section className="leaderboard-page__list">
            <div className="leaderboard-list__header">
              <span>Rank</span>
              <span>Trader</span>
              <span>Trades</span>
              <span>Win Rate</span>
              <span>Profit</span>
              <span>Followers</span>
              <span>Likes</span>
            </div>
            <div className="leaderboard-list__body">
              {rest.map((trader) => (
                <div key={`${activeTab}-${trader.id}`} className="leaderboard-row">
                  <div className="leaderboard-row__rank">
                    <span>{trader.displayRank}</span>
                  </div>
                  <div className="leaderboard-row__user">
                    <img src={trader.avatar} alt={trader.name} className="leaderboard-row__avatar" />
                    <div className="leaderboard-row__info">
                      <span className="leaderboard-row__name">{trader.name}</span>
                      <span className="leaderboard-row__title">{trader.title}</span>
                    </div>
                  </div>
                  <div className="leaderboard-row__trades">{trader.trades}</div>
                  <div className="leaderboard-row__winrate">
                    <div className="leaderboard-row__winrate-bar">
                      <div
                        className="leaderboard-row__winrate-fill"
                        style={{ width: `${Math.min(100, Math.max(0, trader.winRate))}%` }}
                      />
                    </div>
                    <span>{trader.winRate.toFixed(1)}%</span>
                  </div>
                  <div className="leaderboard-row__profit">{trader.profitFormatted}</div>
                  <div className="leaderboard-row__followers">{formatCount(trader.followers)}</div>
                  <div className="leaderboard-row__likes">{formatCount(trader.likes)}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {sortedTraders.length === 0 && (
          <div className="leaderboard-page__empty">
            <BarChart3 size={48} className="leaderboard-page__empty-icon" />
            <h3>No traders yet</h3>
            <p>Start trading to appear on the leaderboard!</p>
          </div>
        )}

        <section className="leaderboard-page__cta">
          <div className="cta-banner">
            <div className="cta-banner__content">
              <Rocket size={32} className="cta-banner__icon" />
              <div>
                <h2 className="cta-banner__title">Ready to compete?</h2>
                <p className="cta-banner__desc">Join elite traders and start climbing the ranks today.</p>
              </div>
            </div>
            {isExternalLink(ctaLink) ? (
              <a
                href={ctaLink.trim()}
                className="cta-banner__btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Get Started</span>
                <ArrowRight size={18} />
              </a>
            ) : (
              <Link to={normalizeInternalLink(ctaLink)} className="cta-banner__btn">
                <span>Get Started</span>
                <ArrowRight size={18} />
              </Link>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Leaderboard;
